import {useAppSelector} from '../../../store/store';
import buttonStyles from '../../../components/button/button.module.scss';
import formStyles from '../../../components/form/form.module.scss';
import {Button} from '../../../components/button/button';
import {__} from '../../../support/lang/translations';
import {ButtonColorType} from '../../../enums/button_color_type.ts';
import {Status} from '../../../enums/status.ts';
import {CenteredSpinner} from '../../../components/spinner/centered_spinner.tsx';
import {Switch} from '../../../components/switch/switch.tsx';
import {classNames} from '../../../support/classnames.ts';
import {SimpleSelect, SimpleSelectOption} from '../../../components/select/simple_select.tsx';
import {ShowElement} from '../../../components/utility/show_element_wrapper.tsx';
import {HideElement} from '../../../components/utility/hide_element_wrapper.tsx';

export function AppointmentDetailsTitle() {
    return (
        <>
            <h1>{__('appointment.appointment_details.title')}</h1>
            <h2>{__('appointment.appointment_details.subtitle')}</h2>
        </>
    );
}

interface ContentProps {
    onMainActivityIdChange: (id: string | null) => void;
    onyReplacementVehicleIdChange: (id: string | null) => void;
    onSubActivityIdsChange: (ids: string[]) => void;
    mainActivityId: string | null;
    subActivityIds: string[];
    replacementVehicleId: string | null;
    locationId: number | null;
    remarks: string;
    onRemarksChange: (remarks: string) => void;
    isEditing: boolean;
    onEditClick: () => void;
}

export function AppointmentDetailsContent(props: ContentProps) {
    const customerInfo = useAppSelector((state) => state.customerInfo);
    const appConfig = useAppSelector((state) => state.appConfig);
    const location = appConfig.locations?.find((location) => location?.id === props.locationId);
    const specialty = location?.specialties?.find(() => true);

    const activities = specialty?.activities || [];
    const replacementVehicles = specialty?.replacement_vehicles || [];

    const mainActivities = activities.filter((activity) => activity?.type === 'main');
    const subActivities = activities.filter((activity) => activity?.type === 'sub');
    const hasMainActivities = mainActivities.length > 0;
    const activityOptions: SimpleSelectOption[] = mainActivities
        ?.filter((activity): activity is NonNullable<typeof activity> => activity != null)
        .map((activity) => {
            return {
                label: activity.name || '',
                value: activity.id || '',
            };
        });
    const replacementVehicleOptions: SimpleSelectOption[] = replacementVehicles
        ?.filter((rv): rv is NonNullable<typeof rv> => rv != null)
        .map((rv) => {
            return {
                label: rv.name || '',
                value: rv.id || '',
            };
        });

    const onSubActivityChange = (subActivityId: string) => {
        if (props.subActivityIds.find((sa) => sa === subActivityId) !== undefined) {
            props.onSubActivityIdsChange(props.subActivityIds.filter((sa) => sa !== subActivityId));
        } else {
            props.onSubActivityIdsChange([...props.subActivityIds, subActivityId]);
        }
    };

    if (customerInfo.status !== Status.FULFILLED) {
        return <CenteredSpinner />;
    }

    return (
        <>
            <h3>{__('appointment.appointment_details.title_appointment')}</h3>
            <div className={classNames(formStyles.form, formStyles.form_centered)}>
                <div className={formStyles.form__grid}>
                    <div className={formStyles.grid__custom}>
                        {hasMainActivities && (
                            <fieldset>
                                <label data-grid-column-start={1} data-grid-column-end={13}>
                                    {__('appointment.appointment_details.fields.main_activity')}
                                    <span className={formStyles.required}>*</span>
                                    <SimpleSelect
                                        value={props.mainActivityId}
                                        defaultValue={props.mainActivityId}
                                        options={activityOptions}
                                        onChange={(value) => props.onMainActivityIdChange(value || null)}
                                        emptyLabel={
                                            __('general.choose_value', {
                                                name: __('appointment.select.fields.activity') as string,
                                            }) as string
                                        }
                                    />
                                </label>
                            </fieldset>
                        )}
                        {subActivities.length === 0 ? null : (
                            <fieldset>
                                <label data-grid-column-start={1} data-grid-column-end={13}>
                                    {hasMainActivities ? (
                                        __('appointment.appointment_details.fields.sub_activities')
                                    ) : (
                                        <>
                                            {__('appointment.appointment_details.fields.activities')}
                                            <span className={formStyles.required}>*</span>
                                        </>
                                    )}
                                    <div>
                                        {subActivities.map((activity, key) => {
                                            return (
                                                <Switch
                                                    key={activity?.id || key}
                                                    disabled={false}
                                                    defaultChecked={
                                                        props.subActivityIds.find((sa) => sa === activity?.id) !==
                                                        undefined
                                                    }
                                                    name={'sub_activities[' + activity?.id + ']'}
                                                    label={activity?.name || ''}
                                                    onChange={() => activity?.id && onSubActivityChange(activity.id)}
                                                />
                                            );
                                        })}
                                    </div>
                                </label>
                            </fieldset>
                        )}
                        <fieldset>
                            <label data-grid-column-start={1} data-grid-column-end={13}>
                                {__('appointment.appointment_details.fields.replacement_vehicle')}
                                <span className={formStyles.required}>*</span>
                                <SimpleSelect
                                    value={props.replacementVehicleId}
                                    defaultValue={props.replacementVehicleId}
                                    options={replacementVehicleOptions}
                                    onChange={(value) => props.onyReplacementVehicleIdChange(value || null)}
                                    emptyLabel={
                                        __('general.choose_value', {
                                            name: __('appointment.select.fields.replacement_vehicle') as string,
                                        }) as string
                                    }
                                />
                            </label>
                        </fieldset>
                        <fieldset>
                            <label data-grid-column-start={1} data-grid-column-end={13}>
                                {__('appointment.appointment_details.fields.remarks')}
                                <textarea
                                    defaultValue={props.remarks}
                                    onChange={(e) => {
                                        props.onRemarksChange(e.target.value);
                                    }}
                                />
                            </label>
                        </fieldset>
                    </div>
                </div>
            </div>
        </>
    );
}

interface FooterProps {
    onPreviousSlide: () => void;
    onNextSlide: () => void;
    nextIsDisabled: boolean;
}

export function AppointmentDetailsFooter(props: FooterProps) {
    return (
        <>
            <ShowElement size={'sm'}>
                <Button
                    className={buttonStyles.button_step}
                    type="button"
                    onClick={() => props.onPreviousSlide()}
                    colorType={ButtonColorType.Black}
                >
                    <i className="fa-light fa-chevron-left" />
                    <span>{__('appointment.appointment_details.footer.buttons.previous')}</span>
                </Button>
            </ShowElement>

            <HideElement size={'sm'}>
                <span />
            </HideElement>

            <Button
                className={buttonStyles.button_step}
                disabled={props.nextIsDisabled}
                type="button"
                onClick={() => props.onNextSlide()}
                colorType={ButtonColorType.Green}
            >
                <i className="fa-light fa-check" />
                <span>{__('appointment.appointment_details.footer.buttons.next')}</span>
            </Button>
        </>
    );
}
